/**
 * PrintingDashboardPage — `/printing` index landing.
 *
 * service-printing exposes printers + templates + jobs + label-batches.
 * This dashboard joins recent jobs (`GET /v1/printing/jobs`) with the printer
 * registry (`GET /v1/printing/printers`).
 */
import type { JSX } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ListOrdered, Printer, XCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import { listPrintJobs, listPrinters } from '../../lib/api/printing';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

export function PrintingDashboardPage(): JSX.Element {
  const jobs = useQuery({
    queryKey: ['printing', 'jobs', 'dashboard'],
    queryFn: () => listPrintJobs({ pageSize: 25 }),
    retry: false,
  });
  const printers = useQuery({
    queryKey: ['printing', 'printers', 'dashboard'],
    queryFn: () => listPrinters({ pageSize: 25 }),
    retry: false,
  });

  const jobsDenied =
    jobs.isError && jobs.error instanceof ApiError && (jobs.error.status === 401 || jobs.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Print management"
        description="Job queue, printers, templates."
        actions={
          <Link
            to="/printing/templates"
            className="px-3 py-2 border border-border rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            Templates
          </Link>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Recent jobs"
          value={jobs.isPending ? '—' : String(jobs.data?.total ?? 0)}
          hint="All recent print jobs"
          icon={Printer}
        />
        <KPICard
          title="Online printers"
          value={
            printers.isPending
              ? '—'
              : String((printers.data?.items ?? []).filter((p) => p.status === 'ONLINE').length)
          }
          hint="status=ONLINE"
          icon={CheckCircle2}
        />
        <KPICard
          title="Failed jobs"
          value={
            jobs.isPending
              ? '—'
              : String((jobs.data?.items ?? []).filter((j) => j.status === 'FAILED').length)
          }
          hint="status=FAILED"
          icon={XCircle}
        />
        <KPICard
          title="Queue length"
          value={
            jobs.isPending
              ? '—'
              : String((jobs.data?.items ?? []).filter((j) => j.status === 'QUEUED').length)
          }
          hint="status=QUEUED"
          icon={ListOrdered}
        />
      </div>

      <section>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">Recent jobs</h2>
        {jobs.isPending ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : jobsDenied ? (
          <DeferredNotice title="Permission required">
            The dev token doesn&apos;t carry <code className="font-mono">Printing/read</code>.
          </DeferredNotice>
        ) : jobs.isError ? (
          <DeferredNotice title="Service unreachable">{(jobs.error as Error).message}</DeferredNotice>
        ) : (jobs.data?.items.length ?? 0) === 0 ? (
          <p className="text-sm text-muted bg-white border border-border rounded-xl p-6 text-center">
            No print jobs yet.
          </p>
        ) : (
          <div className="bg-white border border-border rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                <tr>
                  <th className="text-left px-4 py-2">Job</th>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">Status</th>
                  <th className="text-left px-4 py-2">Created</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {jobs.data?.items.map((j) => (
                  <tr key={j.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{j.id}</td>
                    <td className="px-4 py-2 text-xs">{j.type}</td>
                    <td className="px-4 py-2"><StatusBadge status={j.status} /></td>
                    <td className="px-4 py-2 text-xs text-muted">{new Date(j.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
